// ============================================================================
// AdminOverviewKpi — carte KPI Vue d'ensemble Admin (GEST-ADMIN-IA lot 3)
// ============================================================================
// KpiCard @gs/ui n'a pas de slot hint ni navigation — pattern local calque sur
// OverviewKpi (apps/ug) avec lien natif optionnel (Link react-router).

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

export type AdminOverviewKpiTone = 'blue' | 'green' | 'brand';

const TONES: Record<AdminOverviewKpiTone, string> = {
  blue: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
  green: 'bg-green-50 text-green-600 dark:bg-green-950/40 dark:text-green-400',
  brand: 'bg-brand-50 text-brand-600 dark:bg-brand-950/40 dark:text-brand-400',
};

export interface AdminOverviewKpiProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  tone: AdminOverviewKpiTone;
  /** Hint de metrique — rendu DANS la carte sous la valeur. */
  hint?: ReactNode;
  /** URL de section admin ; absent = carte statique (pas d'affordance). */
  to?: string;
  /** Libelle accessible quand la carte est un lien. */
  activateLabel?: string;
}

const SHELL =
  'rounded-2xl border border-slate-100 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800';

const INTERACTIVE =
  'block cursor-pointer touch-manipulation text-inherit no-underline transition-shadow hover:shadow-md focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500';

export function AdminOverviewKpi({
  label,
  value,
  icon: Icon,
  tone,
  hint,
  to,
  activateLabel,
}: AdminOverviewKpiProps) {
  const body = (
    <>
      <div className="flex items-center gap-4">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${TONES[tone]}`}>
          <Icon size={22} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-meta font-medium text-slate-500 dark:text-slate-400">{label}</p>
          <p className="mt-0.5 text-kpi font-bold leading-none tabular-nums text-slate-800 dark:text-slate-100">
            {value}
          </p>
        </div>
      </div>
      {hint != null ? (
        <p className="mt-2.5 text-meta leading-snug">{hint}</p>
      ) : null}
    </>
  );

  if (to && activateLabel) {
    return (
      <Link to={to} aria-label={activateLabel} className={`${SHELL} ${INTERACTIVE}`}>
        {body}
      </Link>
    );
  }

  return <div className={SHELL}>{body}</div>;
}
